import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    ClipboardCheck,
    Settings,
    AlertTriangle,
    TrendingUp,
    Scale,
    ShieldCheck,
    Store,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { OPS } from "@/constants/testIds";
import QCInspectionSectionsAdmin from "@/components/qc-inspection/QCInspectionSectionsAdmin";
import QCRoundsView from "@/components/qc-inspection/QCRoundsView";
import QCRoundWizard from "@/components/qc-inspection/QCRoundWizard";
import QCViolationsPage from "@/components/qc-inspection/QCViolationsPage";
import QCDashboardView from "@/components/qc-inspection/QCDashboardView";
import QCWastePage from "@/pages/QCWastePage";
import QCBranchNamesView from "@/components/qc-inspection/QCBranchNamesView";

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

/**
 * Unified Quality Management Hub (2026-07-23).
 *
 * Merges the two previous sidebar entries (QC Waste + QC Inspection) into a
 * single "Quality Management" surface with five sub-tabs. Zero backend
 * changes — every existing tab-key permission still gates the same
 * sub-content:
 *
 *   • Dashboard   — qc_inspection permitted
 *   • Rounds      — qc_inspection permitted
 *   • Violations  — qc_inspection permitted
 *   • Waste       — qc_waste       permitted
 *   • Sections    — super_admin (implicitly qc_inspection)
 *
 * Default sub-tab per role (falls back to the first tab the user CAN see):
 *   • inspector      → rounds
 *   • branch_manager → dashboard
 *   • everyone else  → dashboard (if qc_inspection permitted), else waste
 *
 * URL query params supported:
 *   ?tab=<sub>       — pre-selects a sub-tab (must match one the user can see)
 *   ?round=<id>      — opens the round wizard immediately (legacy email deep-link)
 */
export default function QualityHub() {
    const { me, isInspector, canQcViolationManage, canEditQcSections, canQcRoundAdmin } = useAuth();
    const { t } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeRoundId, setActiveRoundId] = useState(null);

    const insPermitted = !!me?.nav?.find((n) => n.key === "qc_inspection")?.permitted;
    const wastePermitted = !!me?.nav?.find((n) => n.key === "qc_waste")?.permitted;

    // ---- Which sub-tabs are visible for THIS caller? ------------------
    const availableTabs = useMemo(() => {
        const list = [];
        if (insPermitted) list.push("dashboard", "rounds", "violations");
        else if (canQcViolationManage) list.push("violations");
        if (wastePermitted) list.push("waste");
        // Checklist editing is capability-driven (`qc_sections`), not role-driven
        // — the backend has always allowed it, the tab used to stay hidden.
        if (canEditQcSections && insPermitted) list.push("sections");
        // Branch renaming (name only) — the QC manager owns the branch labels
        // used all over Quality.
        if (canQcRoundAdmin && insPermitted) list.push("branches");
        return list;
    }, [insPermitted, wastePermitted, canEditQcSections, canQcViolationManage, canQcRoundAdmin]);

    // ---- Default sub-tab decision --------------------------------------
    const defaultTab = useMemo(() => {
        if (availableTabs.length === 0) return null;
        // Honour URL ?tab= when it's one the caller can see
        const requested = searchParams.get("tab");
        if (requested && availableTabs.includes(requested)) return requested;
        if (isInspector && availableTabs.includes("rounds")) return "rounds";
        if (availableTabs.includes("dashboard")) return "dashboard";
        return availableTabs[0];
    }, [availableTabs, isInspector, searchParams]);

    const [activeTab, setActiveTab] = useState(defaultTab);

    // Re-sync activeTab whenever the default (which honours ?tab=) changes.
    useEffect(() => {
        if (defaultTab && activeTab !== defaultTab) {
            setActiveTab(defaultTab);
        }
    }, [defaultTab, activeTab]);

    // ---- Legacy deep-link support: ?round=<id> opens the wizard --------
    useEffect(() => {
        const rid = searchParams.get("round");
        if (rid && !activeRoundId && insPermitted) {
            setActiveRoundId(rid);
            // Clean the URL — don't leave ?round= around after opening
            const next = new URLSearchParams(searchParams);
            next.delete("round");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, activeRoundId, insPermitted, setSearchParams]);

    // ---- Wizard takeover ------------------------------------------------
    if (activeRoundId) {
        return (
            <QCRoundWizard
                inspectionId={activeRoundId}
                onClose={() => setActiveRoundId(null)}
            />
        );
    }

    // ---- Tab change handler updates the URL so it's shareable ----------
    const onTabChange = (val) => {
        setActiveTab(val);
        const next = new URLSearchParams(searchParams);
        next.set("tab", val);
        setSearchParams(next, { replace: true });
    };

    return (
        <div data-testid={OPS.quality.hub} className="space-y-5">
            <PageHeader
                icon={ShieldCheck}
                title={t("quality_hub_title")}
                subtitle={t("quality_hub_subtitle")}
            />
            {availableTabs.length > 0 ? (
                <Tabs value={activeTab || defaultTab} onValueChange={onTabChange} className="space-y-5">
                    <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                        {availableTabs.includes("dashboard") ? (
                            <TabsTrigger data-testid={OPS.quality.tab("dashboard")} value="dashboard" className={triggerClass}>
                                <TrendingUp className="h-4 w-4" />
                                {t("quality_hub_tab_dashboard")}
                            </TabsTrigger>
                        ) : null}
                        {availableTabs.includes("rounds") ? (
                            <TabsTrigger data-testid={OPS.quality.tab("rounds")} value="rounds" className={triggerClass}>
                                <ClipboardCheck className="h-4 w-4" />
                                {t("quality_hub_tab_rounds")}
                            </TabsTrigger>
                        ) : null}
                        {availableTabs.includes("violations") ? (
                            <TabsTrigger data-testid={OPS.quality.tab("violations")} value="violations" className={triggerClass}>
                                <AlertTriangle className="h-4 w-4" />
                                {t("quality_hub_tab_violations")}
                            </TabsTrigger>
                        ) : null}
                        {availableTabs.includes("waste") ? (
                            <TabsTrigger data-testid={OPS.quality.tab("waste")} value="waste" className={triggerClass}>
                                <Scale className="h-4 w-4" />
                                {t("quality_hub_tab_waste")}
                            </TabsTrigger>
                        ) : null}
                        {availableTabs.includes("sections") ? (
                            <TabsTrigger data-testid={OPS.quality.tab("sections")} value="sections" className={triggerClass}>
                                <Settings className="h-4 w-4" />
                                {t("quality_hub_tab_sections")}
                            </TabsTrigger>
                        ) : null}
                        {availableTabs.includes("branches") ? (
                            <TabsTrigger data-testid={OPS.quality.tab("branches")} value="branches" className={triggerClass}>
                                <Store className="h-4 w-4" />
                                {t("quality_hub_tab_branches")}
                            </TabsTrigger>
                        ) : null}
                    </TabsList>
                    {availableTabs.includes("dashboard") ? (
                        <TabsContent value="dashboard" className="mt-0 focus-visible:outline-none">
                            <QCDashboardView />
                        </TabsContent>
                    ) : null}
                    {availableTabs.includes("rounds") ? (
                        <TabsContent value="rounds" className="mt-0 focus-visible:outline-none">
                            <QCRoundsView onOpenRound={setActiveRoundId} />
                        </TabsContent>
                    ) : null}
                    {availableTabs.includes("violations") ? (
                        <TabsContent value="violations" className="mt-0 focus-visible:outline-none">
                            <QCViolationsPage />
                        </TabsContent>
                    ) : null}
                    {availableTabs.includes("waste") ? (
                        <TabsContent value="waste" className="mt-0 focus-visible:outline-none">
                            {/*
                             * QCWastePage renders its own PageHeader — inside the hub we
                             * still show it because its date/branch selectors live in that
                             * header. It sits below the hub's main header which is fine
                             * (same pattern as SalesHub / FinancialsHub).
                             */}
                            <QCWastePage embedded />
                        </TabsContent>
                    ) : null}
                    {availableTabs.includes("sections") ? (
                        <TabsContent value="sections" className="mt-0 focus-visible:outline-none">
                            <QCInspectionSectionsAdmin />
                        </TabsContent>
                    ) : null}
                    {availableTabs.includes("branches") ? (
                        <TabsContent value="branches" className="mt-0 focus-visible:outline-none">
                            <QCBranchNamesView />
                        </TabsContent>
                    ) : null}
                </Tabs>
            ) : null}
        </div>
    );
}
